import {
  fillPrompt,
  variableNames,
  type ContextSlots,
  type PromptVariable,
} from '@/components/ui/prompt-library-clean';
import type { PromptContextName } from '@/types/prompts';

import {
  PromptChipInput,
  PromptTemplate,
  PromptTemplateChip,
  PromptTemplateVariable,
} from './SlashExtension.types';

/** A resolved template as a chip: title shown, body sent, slots kept for a reopen. */
export const templateChip = (template: PromptTemplate): PromptTemplateChip => ({
  templateId: template.id,
  version: template.version,
  title: template.title,
  body: template.body,
  templateBody: template.body,
  variables: template.variables ?? [],
  values: {},
  edited: false,
});

/** The chip's template text, with its `{name}` slots. */
export const templateBodyOf = (chip: PromptTemplateChip) =>
  chip.templateBody || chip.body;

/**
 * What a chat knows for Context Variables (spec FSI #1230): VH3's list, the
 * values found for the chat's subject, and the subject's kind (for the fill
 * step's marker). Empty with the switch off, while the lookup is pending, or
 * when it failed: then no name is a Context Variable and every `{name}` is a
 * typed variable, as before.
 */
export type FillContext = {
  names: PromptContextName[];
  values: Record<string, string>;
  /** "from Job", "from document" or "from quote"; none without a subject. */
  marker?: string;
  /** There is no list: the lookup failed, or has not answered yet. */
  failed?: boolean;
};

export const NO_FILL_CONTEXT: FillContext = { names: [], values: {} };

const DOTTED_NAME = /\{([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)+)\}/g;

/**
 * The list to read one body against: VH3's list, or, when the lookup failed
 * and there is none, each dotted name the body holds, labelled by itself. A
 * failed lookup counts as nothing found: the fields are there and empty,
 * nothing is blocked, and no `{job.reference}` is sent as written.
 */
export const listFor = (
  body: string,
  context: FillContext
): PromptContextName[] => {
  if (context.names.length > 0 || !context.failed) return context.names;
  return [
    ...new Set([...body.matchAll(DOTTED_NAME)].map(match => match[1])),
  ].map(name => ({
    name,
    label: name,
    group: 'Everywhere',
    subjects: ['everywhere'],
    fills_in: '',
    hint: '',
  }));
};

/** The list as the vendored fields take it: labelled slots by name. */
export const contextSlots = (names: PromptContextName[] = []): ContextSlots =>
  Object.fromEntries(names.map(item => [item.name, { label: item.label }]));

/** A name a chat fills from its subject, not from the Everywhere set. */
export const isSubjectName = (item: PromptContextName | undefined) =>
  !!item && !item.subjects.includes('everywhere');

/**
 * The names a user fills, each once, in order. A dotted name
 * (`{job.reference}`) is among them only when it is on VH3's list; any other
 * dotted name stays as written. `{today}` and the other listed names fill
 * from the chat when it knows them.
 */
export const fillNames = (
  templateBody: string,
  names: PromptContextName[] = []
) => variableNames(templateBody, contextSlots(names));

/** A template chip with at least one `{name}` to fill. */
export const needsFill = (
  chip: PromptChipInput | undefined,
  context: FillContext = NO_FILL_CONTEXT
): chip is PromptTemplateChip =>
  !!chip &&
  typeof chip !== 'string' &&
  fillNames(templateBodyOf(chip), listFor(templateBodyOf(chip), context))
    .length > 0;

const hasOwn = (object: object, key: string) =>
  Object.prototype.hasOwnProperty.call(object, key);

/**
 * The values a fill step starts from: the chip's own, else what the chat
 * found for a Context Variable, else each default. A chip's own value wins
 * when it has text, so a chip keeps what it was filled with when the chat
 * moves on. Own keys only, so a variable named like `constructor` reads as
 * unset, never as an `Object.prototype` member; every name gets its own entry.
 */
export const startValues = (
  chip: PromptTemplateChip,
  context: FillContext = NO_FILL_CONTEXT
) => {
  const own = chip.values ?? {};
  const list = listFor(templateBodyOf(chip), context);
  const listed = new Set(list.map(item => item.name));
  return Object.fromEntries(
    fillNames(templateBodyOf(chip), list).map(name => {
      const variable = chip.variables?.find(item => item.name === name);
      const value = hasOwn(own, name) ? own[name] : undefined;
      if (listed.has(name)) {
        const found = hasOwn(context.values, name)
          ? context.values[name]
          : undefined;
        return [name, (value?.trim() ? value : found) ?? value ?? ''];
      }
      return [name, value ?? variable?.default ?? ''];
    })
  ) as Record<string, string>;
};

/** The template body with the values in place (the text sent). */
export const applyValues = (
  templateBody: string,
  values: Record<string, string>,
  names: PromptContextName[] = []
) => fillPrompt(templateBody, values, contextSlots(names));

/** The endpoint's variable list as Prompt Library's map by name. */
export const variablesByName = (variables: PromptTemplateVariable[] = []) =>
  Object.fromEntries(
    variables.map(({ name, description, default: value }) => [
      name,
      { description, default: value } satisfies PromptVariable,
    ])
  );
